import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const getStrength = (pwd) => {
  if (!pwd) return { score: 0, label: "", color: "" };
  let score = 0;
  if (pwd.length >= 8)  score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;

  const levels = [
    { label: "Too short",   color: "#ef4444" },
    { label: "Weak",        color: "#f97316" },
    { label: "Fair",        color: "#eab308" },
    { label: "Good",        color: "#84cc16" },
    { label: "Strong",      color: "#22c55e" },
    { label: "Very strong", color: "#10b981" },
  ];
  return { score, ...levels[score] };
};

const EyeIcon = ({ open }) =>
  open ? (
    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ) : (
    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );

const CheckIcon = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#22c55e" strokeWidth="2.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="2.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const Rule = ({ met, text }) => (
  <div className="d-flex align-items-center gap-2" style={{ fontSize: "0.78rem", color: met ? "#166534" : "#64748b" }}>
    {met ? <CheckIcon /> : <XIcon />}
    <span>{text}</span>
  </div>
);

const Card = ({ children }) => (
  <div style={{
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "2rem 1rem",
    fontFamily: "'DM Sans', system-ui, sans-serif",
  }}>
    <div style={{
      position: "fixed", top: "15%", left: "10%",
      width: 300, height: 300, borderRadius: "50%",
      background: "radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)",
      pointerEvents: "none"
    }} />
    <div style={{
      position: "fixed", bottom: "10%", right: "8%",
      width: 250, height: 250, borderRadius: "50%",
      background: "radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)",
      pointerEvents: "none"
    }} />
    <div style={{
      background: "rgba(255,255,255,0.04)",
      backdropFilter: "blur(20px)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: 20,
      padding: "2.5rem",
      width: "100%",
      maxWidth: 440,
      boxShadow: "0 25px 60px rgba(0,0,0,0.4)",
      position: "relative",
      zIndex: 1,
    }}>
      {children}
    </div>
  </div>
);

const goToLogin = () => {
  localStorage.removeItem("user");
  localStorage.removeItem("last_path");
  localStorage.removeItem("selectedTenantId");
  localStorage.removeItem("active_org_ids");
  window.location.href = "/login";
};

const SetPassword = () => {
  const { token } = useParams();

  const [phase, setPhase] = useState("loading");
  const [serverError, setServerError] = useState("");
  const [form, setForm] = useState({ newPassword: "", confirmPassword: "" });
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [focusField, setFocusField] = useState(null);
  const [isReset, setIsReset] = useState(false);

  const strength = getStrength(form.newPassword);

  const API_BASE_URL = window.__ENV__?.API_BASE_URL || 'http://localhost:5000';

  useEffect(() => {
    localStorage.clear();   // try both common key names
    sessionStorage.clear();    
    // Only remove last_path — don't wipe auth token or entire localStorage
    localStorage.removeItem("last_path");

    if (!token) { setPhase("invalid"); return; }

    axios
      .get(`${API_BASE_URL}/admin/users/changepassword/${token}`)
      .then((res) => {
        const data = res.data?.data || res.data || {};
        const reset = data.is_reset === true;
        setIsReset(reset);

        // Already verified and not a reset link — link already used, redirect to login
        if (data.is_email_verified === true && !reset) {
          window.location.href = "/login";
          return;
        }
        setPhase("valid");
      })
      .catch((err) => {
        const status = err?.response?.status;
        const msg    = err?.response?.data?.message || "";
        if (status === 400 && msg.toLowerCase().includes("expir")) {
          setPhase("expired");
        } else if (status === 400 && msg.toLowerCase().includes("already been used")) {
          //  Already used — redirect straight to login
          window.location.href = "/login";
        } else {
          setPhase("invalid");
        }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setFieldErrors(prev => ({ ...prev, [name]: "" }));
    setServerError("");
  };

  const validate = () => {
    const errs = {};
    if (!form.newPassword) errs.newPassword = "Password is required";
    else if (rules.some((r) => !r.met)) errs.newPassword = "Password does not meet all requirements";

    if (!form.confirmPassword) errs.confirmPassword = "Please confirm your password";
    else if (form.newPassword !== form.confirmPassword) errs.confirmPassword = "Passwords do not match";

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setServerError("");

    try {
      await axios.post(`${API_BASE_URL}/admin/users/changepassword/${token}`, {
        new_password: form.newPassword,
        confirm_password: form.confirmPassword,
      });
      setPhase("success");
    } catch (err) {
      const msg = err?.response?.data?.message || "Something went wrong. Please try again.";
      if (msg.toLowerCase().includes("expir")) {
        setPhase("expired");
      } else {
        setServerError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  const pwd = form.newPassword;
  const rules = [
    { met: pwd.length >= 8,               text: "At least 8 characters" },
    { met: /[A-Z]/.test(pwd),             text: "One uppercase letter" },
    { met: /[a-z]/.test(pwd),             text: "One lowercase letter" },
    { met: /[0-9]/.test(pwd),             text: "One number" },
    { met: /[!@#$%^&*(),.?":{}|<>]/.test(pwd), text: "One special character" },
  ];

  // ── LOADING ──────────────────────────────────────────────────────────────
  if (phase === "loading") {
    return (
      <Card>
        <div className="text-center py-4">
          <div className="spinner-border mb-3" style={{ color: "#818cf8", width: 40, height: 40 }} role="status" />
          <p style={{ color: "#94a3b8", margin: 0 }}>Validating your link…</p>
        </div>
      </Card>
    );
  }

  // ── EXPIRED ──────────────────────────────────────────────────────────────
  if (phase === "expired") {
    return (
      <Card>
        <div className="text-center">
          <div style={{
            width: 64, height: 64, borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(239,68,68,0.2), rgba(239,68,68,0.05))",
            border: "1px solid rgba(239,68,68,0.3)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 1.25rem"
          }}>
            <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#ef4444" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h4 style={{ color: "#f1f5f9", fontWeight: 700, marginBottom: "0.5rem" }}>Link Expired</h4>
          <p style={{ color: "#94a3b8", fontSize: "0.9rem", marginBottom: "1.75rem" }}>
            This password setup link has expired. Please ask your administrator to regenerate a new link.
          </p>
          <button
            onClick={goToLogin}
            style={{
              display: "inline-block", padding: "0.6rem 1.75rem", borderRadius: 10,
              background: "linear-gradient(135deg, #6366f1, #818cf8)",
              color: "#fff", border: "none", cursor: "pointer",
              fontWeight: 600, fontSize: "0.9rem",
            }}
          >
            Back to Login
          </button>
        </div>
      </Card>
    );
  }

  // ── INVALID ───────────────────────────────────────────────────────────────
  if (phase === "invalid") {
    return (
      <Card>
        <div className="text-center">
          <div style={{
            width: 64, height: 64, borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(239,68,68,0.2), rgba(239,68,68,0.05))",
            border: "1px solid rgba(239,68,68,0.3)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 1.25rem"
          }}>
            <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#ef4444" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h4 style={{ color: "#f1f5f9", fontWeight: 700, marginBottom: "0.5rem" }}>Invalid Link</h4>
          <p style={{ color: "#94a3b8", fontSize: "0.9rem", marginBottom: "1.75rem" }}>
            This link is invalid or has already been used. Please contact your administrator.
          </p>
          <button
            onClick={goToLogin}
            style={{
              display: "inline-block", padding: "0.6rem 1.75rem", borderRadius: 10,
              background: "linear-gradient(135deg, #6366f1, #818cf8)",
              color: "#fff", border: "none", cursor: "pointer",
              fontWeight: 600, fontSize: "0.9rem",
            }}
          >
            Back to Login
          </button>
        </div>
      </Card>
    );
  }

  // ── SUCCESS ───────────────────────────────────────────────────────────────
  if (phase === "success") {
    return (
      <Card>
        <div className="text-center">
          <div style={{
            width: 64, height: 64, borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(16,185,129,0.25), rgba(16,185,129,0.05))",
            border: "1px solid rgba(16,185,129,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 1.25rem"
          }}>
            <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#10b981" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h4 style={{ color: "#f1f5f9", fontWeight: 700, marginBottom: "0.5rem" }}>
            {isReset ? "Password Reset!" : "Password Set!"}
          </h4>
          <p style={{ color: "#94a3b8", fontSize: "0.9rem", marginBottom: "1.75rem" }}>
            {isReset
              ? "Your password has been reset successfully. You can now log in."
              : "Your password has been set successfully. You can now log in to your account."}
          </p>
          <button
            onClick={goToLogin}
            style={{
              padding: "0.65rem 2rem", borderRadius: 10,
              background: "linear-gradient(135deg, #10b981, #059669)",
              color: "#fff", border: "none", cursor: "pointer",
              fontWeight: 600, fontSize: "0.9rem",
              width: "100%", textAlign: "center",
            }}
          >
            Go to Login
          </button>
        </div>
      </Card>
    );
  }

  // ── MAIN FORM ─────────────────────────────────────────────────────────────
  return (
    <Card>
      <div className="text-center mb-4">
        <div style={{
          width: 52, height: 52, borderRadius: 14,
          background: "linear-gradient(135deg, #6366f1, #818cf8)",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 1rem",
          boxShadow: "0 8px 24px rgba(99,102,241,0.4)"
        }}>
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h4 style={{ color: "#f1f5f9", fontWeight: 700, marginBottom: "0.25rem", fontSize: "1.35rem" }}>
          {isReset ? "Reset Your Password" : "Set Your Password"}
        </h4>
        <p style={{ color: "#64748b", fontSize: "0.85rem", margin: 0 }}>
          {isReset ? "Enter a new password for your account" : "Create a strong password to activate your account"}
        </p>
      </div>

      {serverError && (
        <div style={{
          background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
          borderRadius: 10, padding: "0.75rem 1rem", marginBottom: "1.25rem",
          color: "#fca5a5", fontSize: "0.85rem", display: "flex", gap: "0.5rem", alignItems: "flex-start"
        }}>
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {serverError}
        </div>
      )}

      {/* New Password */}
      <div className="mb-3">
        <label style={{ color: "#94a3b8", fontSize: "0.8rem", fontWeight: 500, marginBottom: "0.4rem", display: "block", letterSpacing: "0.02em" }}>
          NEW PASSWORD
        </label>
        <div style={{ position: "relative" }}>
          <input
            type={showNew ? "text" : "password"}
            name="newPassword"
            value={form.newPassword}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Enter your new password"
            autoComplete="new-password"
            style={{
              width: "100%",
              background: "rgba(255,255,255,0.06)",
              border: `1px solid ${
                fieldErrors.newPassword
                  ? "#ef4444"
                  : focusField === "newPassword"
                  ? "#6366f1"
                  : "rgba(255,255,255,0.12)"
              }`,
              borderRadius: 10,
              padding: "0.65rem 2.5rem 0.65rem 0.9rem",
              color: "#f1f5f9",
              fontSize: "0.9rem",
              outline: "none",
              transition: "border-color 0.2s",
              boxSizing: "border-box",
            }}
            onFocus={() => setFocusField("newPassword")}
            onBlur={() => setFocusField(null)}
          />
          <button type="button" onClick={() => setShowNew(v => !v)} title={showNew ? "Hide password" : "Show password"} aria-label={showNew ? "Hide password" : "Show password"} style={{
            position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 2,
            display: "flex", alignItems: "center"
          }}>
            <EyeIcon open={showNew} />
          </button>
        </div>
        {fieldErrors.newPassword && (
          <p style={{ color: "#f87171", fontSize: "0.77rem", margin: "0.3rem 0 0 0.1rem" }}>
            {fieldErrors.newPassword}
          </p>
        )}
        <div className="mt-2">
          {form.newPassword && (
            <>
              <div style={{ display: "flex", gap: 3, marginBottom: 4 }}>
                {[1,2,3,4,5].map(i => (
                  <div key={i} style={{
                    flex: 1, height: 3, borderRadius: 4,
                    background: i <= strength.score ? strength.color : "rgba(255,255,255,0.08)",
                    transition: "background 0.3s"
                  }} />
                ))}
              </div>
              <p style={{ color: strength.color, fontSize: "0.75rem", margin: 0, fontWeight: 500 }}>
                {strength.label}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Confirm Password */}
      <div className="mb-4">
        <label style={{ color: "#94a3b8", fontSize: "0.8rem", fontWeight: 500, marginBottom: "0.4rem", display: "block", letterSpacing: "0.02em" }}>
          CONFIRM PASSWORD
        </label>
        <div style={{ position: "relative" }}>
          <input
            type={showConfirm ? "text" : "password"}
            name="confirmPassword"
            value={form.confirmPassword}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Confirm your new password"
            autoComplete="new-password"
            style={{
              width: "100%",
              background: "rgba(255,255,255,0.06)",
              border: `1px solid ${
                fieldErrors.confirmPassword
                  ? "#ef4444"
                  : focusField === "confirmPassword"
                  ? "#6366f1"
                  : "rgba(255,255,255,0.12)"
              }`,
              borderRadius: 10,
              padding: "0.65rem 2.5rem 0.65rem 0.9rem",
              color: "#f1f5f9",
              fontSize: "0.9rem",
              outline: "none",
              boxSizing: "border-box",
            }}
            onFocus={() => setFocusField("confirmPassword")}
            onBlur={() => setFocusField(null)}
          />
          <button type="button" onClick={() => setShowConfirm(v => !v)} title={showConfirm ? "Hide password" : "Show password"} aria-label={showConfirm ? "Hide password" : "Show password"} style={{
            position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 2,
            display: "flex", alignItems: "center"
          }}>
            <EyeIcon open={showConfirm} />
          </button>
        </div>
        {fieldErrors.confirmPassword && (
          <p style={{ color: "#f87171", fontSize: "0.77rem", margin: "0.3rem 0 0 0.1rem" }}>
            {fieldErrors.confirmPassword}
          </p>
        )}
        {form.confirmPassword && form.newPassword && (
          <p style={{
            fontSize: "0.77rem", margin: "0.3rem 0 0 0.1rem",
            color: form.newPassword === form.confirmPassword ? "#22c55e" : "#f87171"
          }}>
            {form.newPassword === form.confirmPassword ? "✓ Passwords match" : "✗ Passwords do not match"}
          </p>
        )}
      </div>

      {/* Password rules */}
      {form.newPassword && (
        <div style={{
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 10, padding: "0.85rem 1rem", marginBottom: "1.25rem",
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.4rem",
        }}>
          {rules.map((r, i) => <Rule key={i} met={r.met} text={r.text} />)}
        </div>
      )}

      {/* Submit */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting}
        style={{
          width: "100%", padding: "0.75rem", borderRadius: 10,
          background: submitting ? "rgba(99,102,241,0.5)" : "linear-gradient(135deg, #6366f1, #818cf8)",
          color: "#fff", border: "none", cursor: submitting ? "not-allowed" : "pointer",
          fontWeight: 600, fontSize: "0.95rem",
          display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
          transition: "opacity 0.2s",
          boxShadow: submitting ? "none" : "0 4px 15px rgba(99,102,241,0.4)"
        }}
      >
        {submitting ? (
          <>
            <span className="spinner-border spinner-border-sm" style={{ width: 16, height: 16 }} />
            Setting password…
          </>
        ) : (
          <>
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            {isReset ? "Reset Password" : "Set Password & Activate Account"}
          </>
        )}
      </button>

      {/* Sign in link */}
      <p style={{ textAlign: "center", marginTop: "1.25rem", marginBottom: 0, fontSize: "0.82rem", color: "#475569" }}>
        Already have an account?{" "}
        <span
          onClick={goToLogin}
          style={{ color: "#818cf8", textDecoration: "none", fontWeight: 500, cursor: "pointer" }}
        >
          Sign in
        </span>
      </p>
    </Card>
  );
};

export default SetPassword;
